import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import MapErrorBoundary from './MapErrorBoundary';
import { fetchGeocode, buildRoomGeocodeQuery, spreadCoordinate } from '../utils/meetingMapGeocode';
import 'leaflet/dist/leaflet.css';
import './MeetingSearchResultsMap.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const MAP_TILE = {
  url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/">CARTO</a>',
};

const parseCoord = (value) => {
  if (value == null || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const getMeetingRoomCoordinates = (room) => {
  if (!room) return null;
  const loc = room.locationCoords;
  if (loc && typeof loc === 'object') {
    const lat = parseCoord(loc.lat);
    const lng = parseCoord(loc.lng ?? loc.lon);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      return { lat, lng };
    }
  }
  const pairs = [
    [room.latitude, room.longitude],
    [room.lat, room.lng],
  ];
  for (const [a, b] of pairs) {
    const lat = parseCoord(a);
    const lng = parseCoord(b);
    if (lat != null && lng != null && (lat !== 0 || lng !== 0)) {
      return { lat, lng };
    }
  }
  return null;
};

const pricePinIcon = (label, isActive) =>
  L.divIcon({
    className: 'msrm-pin-root',
    html: `<div class="msrm-price-pin ${isActive ? 'msrm-price-pin--active' : ''}" title="${label}">${label}</div>`,
    iconSize: [96, 36],
    iconAnchor: [48, 36],
  });

const FitBounds = ({ points, defaultCenter }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
    } else if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 14);
    } else if (defaultCenter) {
      map.setView([defaultCenter.lat, defaultCenter.lng], 12);
    }
  }, [map, points, defaultCenter]);
  return null;
};

const MeetingSearchResultsMap = ({
  rooms = [],
  selectedRoomId,
  onSelectRoom,
  searchLocation = '',
  defaultCenter = { lat: 12.9716, lng: 77.5946 },
}) => {
  const [mapSearch, setMapSearch] = useState('');
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [pins, setPins] = useState([]);
  const [geocoding, setGeocoding] = useState(false);

  const roomsKey = useMemo(
    () => rooms.map((r) => `${r.id || r._id}-${r.pricePerHour}`).join('|'),
    [rooms]
  );

  useEffect(() => {
    let cancelled = false;

    const loadPins = async () => {
      setGeocoding(true);
      const search = String(searchLocation || '').trim();

      let center = defaultCenter;
      if (search) {
        const cityCoords = await fetchGeocode(`${search}, India`);
        if (cityCoords && !cancelled) {
          center = cityCoords;
          setMapCenter(cityCoords);
        }
      } else if (!cancelled) {
        setMapCenter(defaultCenter);
        center = defaultCenter;
      }

      const resolved = [];
      for (let i = 0; i < rooms.length; i += 1) {
        if (cancelled) return;
        const room = rooms[i];
        const id = room._id || room.id;
        let coords = getMeetingRoomCoordinates(room);

        if (!coords) {
          const query = buildRoomGeocodeQuery(room, search);
          coords = await fetchGeocode(query);
          if (!coords && search) {
            coords = await fetchGeocode(`${room.name || 'Meeting room'}, ${search}, India`);
          }
          if (!coords && center) {
            coords = spreadCoordinate(center.lat, center.lng, i, rooms.length);
          }
        } else if (rooms.length > 1) {
          coords = spreadCoordinate(coords.lat, coords.lng, i, rooms.length);
        }

        if (!coords) continue;

        const price = Number(room.pricePerHour) || 0;
        const label =
          price > 0
            ? `₹${Math.round(price).toLocaleString('en-IN')}/hr`
            : 'On request';

        resolved.push({
          room,
          id,
          lat: coords.lat,
          lng: coords.lng,
          label,
          name: room.name || 'Room',
          image: room.image,
        });

        await new Promise((r) => setTimeout(r, 120));
      }

      if (!cancelled) {
        setPins(resolved);
        setGeocoding(false);
      }
    };

    loadPins();
    return () => {
      cancelled = true;
    };
  }, [roomsKey, searchLocation, defaultCenter.lat, defaultCenter.lng]);

  const filtered = useMemo(() => {
    const q = mapSearch.trim().toLowerCase();
    if (!q) return pins;
    return pins.filter((m) => {
      const hay = `${m.name} ${m.room.location || ''} ${m.room.city || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [pins, mapSearch]);

  const points = pins.map((m) => ({ lat: m.lat, lng: m.lng }));

  return (
    <aside className="msr-map-panel">
      <div className="msr-map-search">
        <input
          type="text"
          value={mapSearch}
          onChange={(e) => setMapSearch(e.target.value)}
          placeholder="Search on map"
          aria-label="Search on map"
        />
      </div>
      <div className="msr-map-wrap">
        {geocoding && (
          <div className="msr-map-loading">
            <span>Loading prices on map…</span>
          </div>
        )}
        <MapErrorBoundary>
          <MapContainer
            key={`${mapCenter.lat}-${mapCenter.lng}-${pins.length}`}
            center={[mapCenter.lat, mapCenter.lng]}
            zoom={12}
            className="msr-leaflet"
            scrollWheelZoom
          >
            <TileLayer url={MAP_TILE.url} attribution={MAP_TILE.attribution} />
            <FitBounds points={points} defaultCenter={mapCenter} />
            {filtered.map((pin) => {
              const active = selectedRoomId && String(selectedRoomId) === String(pin.id);
              return (
                <Marker
                  key={pin.id}
                  position={[pin.lat, pin.lng]}
                  icon={pricePinIcon(pin.label, active)}
                  eventHandlers={{ click: () => onSelectRoom?.(pin.room) }}
                >
                  <Popup>
                    <strong>{pin.name}</strong>
                    <br />
                    {pin.label}
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </MapErrorBoundary>
        {!geocoding && !pins.length && rooms.length > 0 && (
          <div className="msr-map-empty">
            <p>Could not place rooms on map. Check addresses in room listings.</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default MeetingSearchResultsMap;
